import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { positionEventEmitter } from '../services/positionEventEmitter';

export interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'trade_pnl' | 'fee';
  amount: number;
  balance: number;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  method?: string;
  description: string;
  timestamp: Date;
  reference?: string;
}

export interface AccountDetails {
  accountId: string;
  accountType: 'demo' | 'live';
  status: 'active' | 'suspended' | 'pending';
  kycStatus: 'pending' | 'verified' | 'rejected';
  userType: 'standard' | 'premium' | 'vip';
  createdAt: Date;
  lastLogin: Date;
}

export interface AccountBalance {
  demoBalance: number;
  liveBalance: number;
  equity: number;
  margin: number;
  freeMargin: number;
  marginLevel: number;
  profit: number; // Realized profit from closed positions only
  leverage: string;
}

interface AccountContextType {
  accountBalance: AccountBalance;
  transactions: Transaction[];
  accountDetails: AccountDetails | null;
  isLoading: boolean;
  deposit: (amount: number, method: string) => Promise<Transaction>;
  withdraw: (amount: number, method: string) => Promise<Transaction>;
  getTransactions: () => Promise<Transaction[]>;
  getAccountDetails: () => Promise<AccountDetails>;
  updateBalance: (type: 'demo' | 'live', amount: number) => void;
  addProfit: (pnlAmount: number, marginReturn: number, description: string, accountType?: 'demo' | 'live') => void;
  updateUsedMargin: (usedMargin: number) => void;
  updateEquity: (unrealizedPnL: number, accountType?: 'demo' | 'live') => void;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

interface AccountProviderProps {
  children: ReactNode;
}

export const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [accountBalance, setAccountBalance] = useState<AccountBalance>({
    demoBalance: 1000,
    liveBalance: 0,
    equity: 1000, // Start with demo balance as equity
    margin: 0,
    freeMargin: 1000, // Start with demo balance as free margin
    marginLevel: 0,
    profit: 0,
    leverage: '100:1',
  });
  
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Listen to position updates and update account balance accordingly
  useEffect(() => {
    const handlePositionUpdate = (data: { totalUsedMargin: number; totalUnrealizedPnL: number; accountType: string }) => {
      setAccountBalance(prev => {
        const currentBalance = data.accountType === 'demo' ? prev.demoBalance : prev.liveBalance;
        const newEquity = currentBalance + data.totalUnrealizedPnL;
        const newFreeMargin = newEquity - data.totalUsedMargin;
        const newMarginLevel = data.totalUsedMargin > 0 ? (newEquity / data.totalUsedMargin) * 100 : 0;
        
        return {
          ...prev,
          equity: newEquity,
          margin: data.totalUsedMargin,
          freeMargin: Math.max(0, newFreeMargin),
          marginLevel: newMarginLevel,
        };
      });
    };

    positionEventEmitter.on('positionsUpdated', handlePositionUpdate);

    return () => {
      positionEventEmitter.off('positionsUpdated', handlePositionUpdate);
    };
  }, []);

  // Mock transactions data
  useEffect(() => {
    if (user) {
      const mockTransactions: Transaction[] = [
        {
          id: '1',
          type: 'deposit',
          amount: 5000,
          balance: 25000,
          status: 'completed',
          method: 'Bank Transfer',
          description: 'Initial deposit via bank transfer',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
          reference: 'TXN-001',
        },
        {
          id: '2',
          type: 'trade_pnl',
          amount: 42,
          balance: 24850,
          status: 'completed',
          description: 'EURUSD position closed with profit',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
        },
        {
          id: '3',
          type: 'withdrawal',
          amount: 2500,
          balance: 22500,
          status: 'completed',
          method: 'Bank Transfer',
          description: 'Withdrawal to bank account',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
          reference: 'TXN-002',
        },
        {
          id: '4',
          type: 'trade_pnl',
          amount: -125,
          balance: 22375,
          status: 'completed',
          description: 'GBPUSD position closed with loss',
          timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
        },
        {
          id: '5',
          type: 'deposit',
          amount: 3000,
          balance: 25375,
          status: 'completed',
          method: 'UPI',
          description: 'Additional deposit via UPI',
          timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
          reference: 'TXN-003',
        },
        {
          id: '6',
          type: 'fee',
          amount: 25,
          balance: 25350,
          status: 'completed',
          description: 'Monthly account maintenance fee',
          timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 1 week ago
        },
        {
          id: '7',
          type: 'trade_pnl',
          amount: 89,
          balance: 25439,
          status: 'completed',
          description: 'BTC position closed with profit',
          timestamp: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000), // 8 days ago
        },
        {
          id: '8',
          type: 'deposit',
          amount: 1500,
          balance: 26939,
          status: 'completed',
          method: 'Cryptocurrency',
          description: 'Deposit via Bitcoin transfer',
          timestamp: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000), // 10 days ago
          reference: 'TXN-004',
        },
        {
          id: '9',
          type: 'trade_pnl',
          amount: -67,
          balance: 26872,
          status: 'completed',
          description: 'GOLD position closed with loss',
          timestamp: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000), // 12 days ago
        },
        {
          id: '10',
          type: 'withdrawal',
          amount: 1000,
          balance: 25872,
          status: 'completed',
          method: 'PayPal',
          description: 'Withdrawal to PayPal account',
          timestamp: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
          reference: 'TXN-005',
        },
        {
          id: '11',
          type: 'fee',
          amount: 15,
          balance: 25857,
          status: 'completed',
          description: 'Trading platform usage fee',
          timestamp: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000), // 20 days ago
        },
      ];
      setTransactions(mockTransactions);
    }
  }, [user]);

  // Mock account details
  useEffect(() => {
    if (user) {
      const mockAccountDetails: AccountDetails = {
        accountId: 'LTS-2024-001',
        accountType: 'live',
        status: 'active',
        kycStatus: 'verified',
        userType: 'standard',
        createdAt: new Date('2024-01-01'),
        lastLogin: new Date(),
      };
      setAccountDetails(mockAccountDetails);
    }
  }, [user]);

  const deposit = async (amount: number, method: string): Promise<Transaction> => {
    setIsLoading(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newTransaction: Transaction = {
        id: Date.now().toString(),
        type: 'deposit',
        amount,
        balance: accountBalance.liveBalance + amount,
        status: 'completed',
        method,
        description: `Deposit via ${method}`,
        timestamp: new Date(),
        reference: `TXN-${Date.now()}`,
      };

      // Update balance
      const newBalance = accountBalance.liveBalance + amount;
      setAccountBalance(prev => ({
        ...prev,
        liveBalance: newBalance,
        equity: prev.equity + amount,
        freeMargin: prev.freeMargin + amount,
      }));

      // Add transaction
      setTransactions(prev => [newTransaction, ...prev]);

      return newTransaction;
    } finally {
      setIsLoading(false);
    }
  };

  const withdraw = async (amount: number, method: string): Promise<Transaction> => {
    setIsLoading(true);
    
    try {
      // Check if sufficient balance
      if (amount > accountBalance.liveBalance) {
        throw new Error('Insufficient balance');
      }

      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newTransaction: Transaction = {
        id: Date.now().toString(),
        type: 'withdrawal',
        amount,
        balance: accountBalance.liveBalance - amount,
        status: 'completed',
        method,
        description: `Withdrawal via ${method}`,
        timestamp: new Date(),
        reference: `TXN-${Date.now()}`,
      };

      // Update balance
      const newBalance = accountBalance.liveBalance - amount;
      setAccountBalance(prev => ({
        ...prev,
        liveBalance: newBalance,
        equity: prev.equity - amount,
        freeMargin: prev.freeMargin - amount,
      }));

      // Add transaction
      setTransactions(prev => [newTransaction, ...prev]);

      return newTransaction;
    } finally {
      setIsLoading(false);
    }
  };

  const getTransactions = async (): Promise<Transaction[]> => {
    // In a real app, this would fetch from API
    return transactions;
  };

  const getAccountDetails = async (): Promise<AccountDetails> => {
    if (!accountDetails) {
      throw new Error('Account details not found');
    }
    return accountDetails;
  };

  const updateBalance = (type: 'demo' | 'live', amount: number) => {
    if (type === 'demo') {
      setAccountBalance(prev => ({
        ...prev,
        demoBalance: amount,
        equity: amount, // For demo, equity = balance
        freeMargin: Math.max(0, amount - prev.margin),
      }));
    } else {
      setAccountBalance(prev => ({
        ...prev,
        liveBalance: amount,
        equity: amount, // For simplicity, equity = balance
        freeMargin: Math.max(0, amount - prev.margin),
      }));
    }
  };

  const updateUsedMargin = useCallback((usedMargin: number) => {
    setAccountBalance(prev => {
      const newFreeMargin = prev.equity - usedMargin;
      const newMarginLevel = usedMargin > 0 ? (prev.equity / usedMargin) * 100 : 0;
      
      return {
        ...prev,
        margin: usedMargin,
        freeMargin: Math.max(0, newFreeMargin),
        marginLevel: newMarginLevel,
      };
    });
  }, []);

  const updateEquity = useCallback((unrealizedPnL: number, accountType: 'demo' | 'live' = 'live') => {
    setAccountBalance(prev => {
      const currentBalance = accountType === 'demo' ? prev.demoBalance : prev.liveBalance;
      const newEquity = currentBalance + unrealizedPnL;
      const newFreeMargin = newEquity - prev.margin;
      const newMarginLevel = prev.margin > 0 ? (newEquity / prev.margin) * 100 : 0;
      
      return {
        ...prev,
        equity: newEquity,
        freeMargin: Math.max(0, newFreeMargin),
        marginLevel: newMarginLevel,
      };
    });
  }, []);

  const addProfit = (pnlAmount: number, marginReturn: number, description: string, accountType: 'demo' | 'live' = 'live') => {
    const totalAmount = pnlAmount + marginReturn; // Total amount to add to balance
    
    // Update balance and profit
    setAccountBalance(prev => {
      const newBalance = accountType === 'demo' 
        ? prev.demoBalance + totalAmount 
        : prev.liveBalance + totalAmount;
      const newProfit = prev.profit + pnlAmount; // Only add P&L to profit, not margin return
      
      // Calculate new equity: balance + unrealized P&L from remaining open positions
      // Note: This will be updated by the position event emitter with the correct unrealized P&L
      const newEquity = newBalance; // Will be updated by position event emitter
      const newFreeMargin = newEquity - prev.margin; // Free margin = equity - used margin
      
      return {
        ...prev,
        ...(accountType === 'demo' 
          ? { demoBalance: newBalance }
          : { liveBalance: newBalance }
        ),
        profit: newProfit,
        equity: newEquity,
        freeMargin: Math.max(0, newFreeMargin), // Ensure free margin doesn't go negative
      };
    });

    // Add transaction record
    const newTransaction: Transaction = {
      id: Date.now().toString(),
      type: 'trade_pnl',
      amount: totalAmount,
      balance: accountType === 'demo' 
        ? accountBalance.demoBalance + totalAmount 
        : accountBalance.liveBalance + totalAmount,
      status: 'completed',
      description,
      timestamp: new Date(),
    };

    setTransactions(prev => [newTransaction, ...prev]);
  };

  const value: AccountContextType = {
    accountBalance,
    transactions,
    accountDetails,
    isLoading,
    deposit,
    withdraw,
    getTransactions,
    getAccountDetails,
    updateBalance,
    addProfit,
    updateUsedMargin,
    updateEquity,
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};
